import type { IconName, TagColor } from '@billy/design-system'

import { ReviewEntryStatus } from '../../../enums/reviewEntryStatus'
import { ReviewEntryStatusKeyword } from '../../../enums/reviewEntryStatusKeyword'
import { isReviewEntryProcessingStatus } from '../../../utils/isReviewEntryProcessingStatus'

export interface StatusTagConfig {
  color: TagColor
  icon: IconName
  label: string
}

export interface StatusLabels {
  processing: string
  toValidate: string
  validated: string
  written: string
}

const hasApprovedStatus = (status: string): boolean =>
  /\bapproved\b/.test(status) && !/\bunapproved\b/.test(status) && !/\bdisapproved\b/.test(status)

const hasReviewedStatus = (status: string): boolean =>
  /\breviewed\b/.test(status) && !/\bnot[_-]reviewed\b/.test(status)

const hasInvalidatedStatus = (status: string): boolean =>
  /\binvalidated\b/.test(status) && !/\bnot[_-]invalidated\b/.test(status)

export const getStatusTagConfig = (status: string | undefined, labels: StatusLabels): StatusTagConfig => {
  const normalizedStatus = status?.toLowerCase() ?? ''

  if (isReviewEntryProcessingStatus(status)) {
    return { color: 'neutral', icon: 'loaderCircle', label: labels.processing }
  }

  if (normalizedStatus === ReviewEntryStatus.Validated) {
    return { color: 'green', icon: 'check', label: labels.validated }
  }

  if (normalizedStatus === ReviewEntryStatus.Written) {
    return { color: 'blue', icon: 'check', label: labels.written }
  }

  if (hasInvalidatedStatus(normalizedStatus)) {
    return { color: 'red', icon: 'circleAlert', label: status ?? labels.toValidate }
  }

  if (hasReviewedStatus(normalizedStatus)) {
    return { color: 'green', icon: 'check', label: status ?? labels.validated }
  }

  if (hasApprovedStatus(normalizedStatus)) {
    return { color: 'green', icon: 'check', label: labels.validated }
  }

  const isToValidateStatus =
    normalizedStatus === ReviewEntryStatus.Parsed ||
    ((normalizedStatus.includes(ReviewEntryStatusKeyword.Validate) ||
      normalizedStatus.includes(ReviewEntryStatusKeyword.Review)) &&
      !normalizedStatus.includes(ReviewEntryStatusKeyword.Validated) &&
      !normalizedStatus.includes(ReviewEntryStatusKeyword.Invalidated) &&
      !normalizedStatus.includes(ReviewEntryStatusKeyword.Reviewed))

  if (isToValidateStatus) {
    return { color: 'purple', icon: 'target', label: labels.toValidate }
  }

  if (
    normalizedStatus.includes(ReviewEntryStatusKeyword.Warning) ||
    normalizedStatus.includes(ReviewEntryStatusKeyword.Pending)
  ) {
    return { color: 'yellow', icon: 'target', label: status ?? labels.toValidate }
  }

  return { color: 'purple', icon: 'target', label: labels.toValidate }
}
